/**
 * ViewOptionsFields — engine-specific option inputs for CREATE / ALTER VIEW.
 *
 * Per-engine surface (mirrors DSE2 §3.D2 dialect.view emitters):
 *   - mssql:  WITH SCHEMABINDING (checkbox).
 *   - pgsql:  check_option (none | local | cascaded), security_barrier checkbox.
 *   - mysql:  ALGORITHM = …, SQL SECURITY {DEFINER|INVOKER},
 *             WITH {LOCAL|CASCADED} CHECK OPTION, DEFINER = … (verbatim text).
 *   - oracle: FORCE checkbox, EDITIONABLE / NONEDITIONABLE (mutually exclusive).
 *   - sqlite: no editable options here — `ViewBodyEditFormBody` renders the
 *             unsupported branch upstream.
 *
 * The `value` shape is the `viewOptions` slice consumed by
 * `commitAlterView(ctx, { viewName, bodyText, viewOptions })` — i.e. the
 * `ViewDef` minus the fields the form's parent owns (`name`, `bodyText`,
 * `orReplace`, `mssqlOrAlter`).
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice D2 — View / Materialized View UI.
 */
import { Checkbox } from '../../../../../components/ui/checkbox';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../components/ui/select';
import type { SqlEngine } from '../../../../sql-grammar/model';
import type { ViewDef } from '../../../../sql-resolver/types/ddlEdit';

export type ViewOptionsValue = Omit<
  ViewDef,
  'name' | 'bodyText' | 'orReplace' | 'mssqlOrAlter'
>;

export interface ViewOptionsFieldsProps {
  readonly engine: SqlEngine;
  readonly value: ViewOptionsValue;
  readonly onChange: (next: ViewOptionsValue) => void;
  readonly testId: string;
}

const NONE = '__none__' as const;

export function ViewOptionsFields({ engine, value, onChange, testId }: ViewOptionsFieldsProps) {
  if (engine === 'sqlite') return null;

  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Options</div>
      {engine === 'mssql' ? <MssqlFields value={value} onChange={onChange} testId={testId} /> : null}
      {engine === 'postgres' ? (
        <PgsqlFields value={value} onChange={onChange} testId={testId} />
      ) : null}
      {engine === 'mysql' ? <MysqlFields value={value} onChange={onChange} testId={testId} /> : null}
      {engine === 'oracle' ? <OracleFields value={value} onChange={onChange} testId={testId} /> : null}
    </div>
  );
}

interface FieldProps {
  readonly value: ViewOptionsValue;
  readonly onChange: (next: ViewOptionsValue) => void;
  readonly testId: string;
}

function MssqlFields({ value, onChange, testId }: FieldProps) {
  return (
    <label className="flex items-center gap-2 text-xs" data-testid={`${testId}-mssql-schemabinding`}>
      <Checkbox
        checked={value.mssqlWithSchemabinding === true}
        onCheckedChange={(checked) =>
          onChange({ ...value, mssqlWithSchemabinding: checked === true ? true : undefined })
        }
        data-testid={`${testId}-mssql-schemabinding-input`}
      />
      <span>WITH SCHEMABINDING</span>
    </label>
  );
}

function PgsqlFields({ value, onChange, testId }: FieldProps) {
  return (
    <>
      <div className="flex flex-col gap-1">
        <Label htmlFor={`${testId}-pgsql-check-option`} className="text-xs">
          Check option
        </Label>
        <Select
          value={value.pgsqlOptions?.checkOption ?? NONE}
          onValueChange={(v) => {
            const next = v === NONE ? undefined : (v as 'local' | 'cascaded');
            const opts = mergePgsqlOptions(value.pgsqlOptions, { checkOption: next });
            onChange({ ...value, pgsqlOptions: opts });
          }}
        >
          <SelectTrigger id={`${testId}-pgsql-check-option`} className="h-8 text-xs" data-testid={`${testId}-pgsql-check-option`}>
            <SelectValue placeholder="(none)" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>(none)</SelectItem>
            <SelectItem value="local">local</SelectItem>
            <SelectItem value="cascaded">cascaded</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <label className="flex items-center gap-2 text-xs" data-testid={`${testId}-pgsql-security-barrier`}>
        <Checkbox
          checked={value.pgsqlOptions?.securityBarrier === true}
          onCheckedChange={(checked) => {
            const opts = mergePgsqlOptions(value.pgsqlOptions, {
              securityBarrier: checked === true ? true : undefined,
            });
            onChange({ ...value, pgsqlOptions: opts });
          }}
          data-testid={`${testId}-pgsql-security-barrier-input`}
        />
        <span>Security barrier</span>
      </label>
    </>
  );
}

function mergePgsqlOptions(
  current: ViewOptionsValue['pgsqlOptions'],
  patch: Partial<NonNullable<ViewOptionsValue['pgsqlOptions']>>,
): ViewOptionsValue['pgsqlOptions'] {
  const next = { ...(current ?? {}), ...patch };
  if (next.checkOption === undefined && next.securityBarrier === undefined) return undefined;
  return next;
}

function MysqlFields({ value, onChange, testId }: FieldProps) {
  return (
    <>
      <div className="flex flex-col gap-1">
        <Label htmlFor={`${testId}-mysql-algorithm`} className="text-xs">ALGORITHM</Label>
        <Select
          value={value.mysqlAlgorithm ?? NONE}
          onValueChange={(v) =>
            onChange({
              ...value,
              mysqlAlgorithm: v === NONE ? undefined : (v as 'undefined' | 'merge' | 'temptable'),
            })
          }
        >
          <SelectTrigger id={`${testId}-mysql-algorithm`} className="h-8 text-xs" data-testid={`${testId}-mysql-algorithm`}>
            <SelectValue placeholder="(default)" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>(default)</SelectItem>
            <SelectItem value="undefined">UNDEFINED</SelectItem>
            <SelectItem value="merge">MERGE</SelectItem>
            <SelectItem value="temptable">TEMPTABLE</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor={`${testId}-mysql-sql-security`} className="text-xs">SQL SECURITY</Label>
        <Select
          value={value.mysqlSqlSecurity ?? NONE}
          onValueChange={(v) =>
            onChange({
              ...value,
              mysqlSqlSecurity: v === NONE ? undefined : (v as 'definer' | 'invoker'),
            })
          }
        >
          <SelectTrigger id={`${testId}-mysql-sql-security`} className="h-8 text-xs" data-testid={`${testId}-mysql-sql-security`}>
            <SelectValue placeholder="(default)" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>(default)</SelectItem>
            <SelectItem value="definer">DEFINER</SelectItem>
            <SelectItem value="invoker">INVOKER</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor={`${testId}-mysql-check-option`} className="text-xs">WITH CHECK OPTION</Label>
        <Select
          value={value.mysqlCheckOption ?? NONE}
          onValueChange={(v) =>
            onChange({
              ...value,
              mysqlCheckOption: v === NONE ? undefined : (v as 'local' | 'cascaded'),
            })
          }
        >
          <SelectTrigger id={`${testId}-mysql-check-option`} className="h-8 text-xs" data-testid={`${testId}-mysql-check-option`}>
            <SelectValue placeholder="(none)" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>(none)</SelectItem>
            <SelectItem value="local">LOCAL</SelectItem>
            <SelectItem value="cascaded">CASCADED</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor={`${testId}-mysql-definer`} className="text-xs">DEFINER (verbatim)</Label>
        <Input
          id={`${testId}-mysql-definer`}
          data-testid={`${testId}-mysql-definer`}
          value={value.mysqlDefiner ?? ''}
          onChange={(e) =>
            onChange({ ...value, mysqlDefiner: e.target.value.trim() === '' ? undefined : e.target.value })
          }
          placeholder="'admin'@'%' or CURRENT_USER"
          className="h-8 font-mono text-xs"
        />
      </div>
    </>
  );
}

function OracleFields({ value, onChange, testId }: FieldProps) {
  const editionable: 'editionable' | 'noneditionable' | typeof NONE =
    value.oracleEditionable ? 'editionable' : value.oracleNonEditionable ? 'noneditionable' : NONE;
  return (
    <>
      <label className="flex items-center gap-2 text-xs" data-testid={`${testId}-oracle-force`}>
        <Checkbox
          checked={value.oracleForce === true}
          onCheckedChange={(checked) =>
            onChange({ ...value, oracleForce: checked === true ? true : undefined })
          }
          data-testid={`${testId}-oracle-force-input`}
        />
        <span>FORCE</span>
      </label>
      <div className="flex flex-col gap-1">
        <Label htmlFor={`${testId}-oracle-editionable`} className="text-xs">Editionable</Label>
        <Select
          value={editionable}
          onValueChange={(v) =>
            onChange({
              ...value,
              oracleEditionable: v === 'editionable' ? true : undefined,
              oracleNonEditionable: v === 'noneditionable' ? true : undefined,
            })
          }
        >
          <SelectTrigger id={`${testId}-oracle-editionable`} className="h-8 text-xs" data-testid={`${testId}-oracle-editionable`}>
            <SelectValue placeholder="(default)" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>(default)</SelectItem>
            <SelectItem value="editionable">EDITIONABLE</SelectItem>
            <SelectItem value="noneditionable">NONEDITIONABLE</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </>
  );
}
